import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/support/tickets?status=${filter}`, { withCredentials: true });
      setTickets(res.data.tickets || []);
    } catch (err) { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleResolve = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/support/tickets/${id}?status=resolved`, {}, { withCredentials: true });
      toast.success('Ticket resolved');
      fetchTickets();
    } catch (err) { toast.error('Failed to update ticket'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-support">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Support Tickets</h1>
        <p className="text-white/45 text-sm">Manage user support requests</p>
      </div>

      <div className="flex gap-2">
        {['all', 'open', 'resolved'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-space font-bold transition-all ${
              filter === f ? 'shimmer-btn text-white border-0' : 'bg-white/[0.04] text-white/40 border border-white/[0.08] hover:text-white/60'
            }`} data-testid={`filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No {filter === 'all' ? '' : filter} support tickets.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} className="bg-[#0E1830] border border-white/5 rounded-xl p-5" data-testid={`ticket-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium text-sm">{t.subject}</h3>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                      t.status === 'resolved' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}>{t.status || 'open'}</span>
                  </div>
                  <p className="text-white/30 text-xs">{t.user_email} &middot; {new Date(t.created_at).toLocaleDateString()}</p>
                </div>
                {t.status !== 'resolved' && (
                  <Button size="sm" onClick={() => handleResolve(t.id)}
                    className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-full px-4 text-xs"
                    data-testid={`resolve-${t.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Resolve
                  </Button>
                )}
              </div>
              <p className="text-white/50 text-sm leading-relaxed">{t.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
